import { PERMISSIONS } from "./permissionKeys.js";
import { getMyPermissions, hasPermission } from "./permissions.js";
import { getCurrentUser } from "./userInfo.js";
import {
    createTenantView,
    deleteTenantView,
    forkTenantView,
    getTenantView,
    getTenantViewCapabilities,
    hideTenantView,
    listTenantViewDependents,
    listTenantViews,
    publishTenantView,
    unhideTenantView,
    unpublishTenantView,
    updateTenantView,
} from "./tenantViewsApi.js";
import { openTenantViewWriteJournal } from "./tenantViewWriteJournal.js";
import { KERNEL_CANVAS_ARTIFACT_FORMAT_VERSION } from "./kernelCanvasArtifact.js";
import { mergeSameVersionTenantViewDefinition } from "./tenantViewDefinitionMerge.js";
import { AUTH_REQUIRED } from "./featureFlags.js";
import { kernelSnapshotSourceBody } from "./kernelResolver.js";

const PAGE_SIZE = 200;
const DEPENDENCY_GET_CONCURRENCY = 4;
const BODY_LOAD_ATTEMPTS = 3;
const buckets = new Map();
const listeners = new Set();
const storedBodies = new WeakSet();
const storedBodyBuckets = new WeakMap();
let activeTenantId = null;
let identityGeneration = 0;

const EMPTY = Object.freeze({
    tenantId: null,
    status: "idle",
    capabilities: null,
    rows: Object.freeze([]),
    error: null,
});

function tenantKey(tenantId) {
    return tenantId || activeTenantId || "default";
}

function notify() {
    for (const listener of listeners) {
        try {
            listener();
        } catch {
            // Subscriber failures cannot block catalog refresh.
        }
    }
}

export function subscribeTenantViewCloud(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

export function getTenantViewCloudState(tenantId) {
    return buckets.get(tenantKey(tenantId))?.snapshot || EMPTY;
}

// The authenticated App is remounted on a stable user/session/tenant change,
// but this module outlives that React tree. Drop the active bucket before the
// replacement tree mounts and invalidate every pending A-side catalog/body
// load so it cannot repopulate state after B has authenticated.
export function resetTenantViewCloudIdentityState() {
    identityGeneration += 1;
    activeTenantId = null;
    for (const bucket of buckets.values()) {
        bucket.loadSequence = (bucket.loadSequence || 0) + 1;
        bucket.inflight = null;
        bucket.bodyInflight?.clear?.();
    }
    buckets.clear();
    notify();
}

function invalidateCatalogLoad(bucket) {
    if (!bucket?.inflight) return;
    bucket.loadSequence = (bucket.loadSequence || 0) + 1;
    bucket.inflight = null;
    if (bucket.snapshot?.status === "loading") {
        const settled = bucket.settledSnapshot;
        bucket.snapshot = {
            ...bucket.snapshot,
            status: settled?.status || "ready",
            error: settled?.error || null,
        };
    }
}

export function markTenantViewCloudUnavailable(tenantId, error) {
    const bucket = buckets.get(tenantKey(tenantId));
    if (!bucket?.snapshot) return;
    invalidateCatalogLoad(bucket);
    bucket.snapshot = {
        ...bucket.snapshot,
        status: "unavailable",
        error,
    };
    bucket.settledSnapshot = bucket.snapshot;
    notify();
}

function permission(caps, key) {
    return hasPermission(caps, key);
}

function numericTimestamp(value) {
    if (typeof value === "number") return Number.isFinite(value) ? value : null;
    if (typeof value !== "string" || !value) return null;
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : null;
}

function compareVersions(left, right) {
    try {
        const leftVersion = BigInt(String(left));
        const rightVersion = BigInt(String(right));
        return leftVersion === rightVersion ? 0 : leftVersion > rightVersion ? 1 : -1;
    } catch {
        return String(left).localeCompare(String(right));
    }
}

function presentation(view, viewerId, permissions, bucket = null) {
    const metadata = view.metadata;
    const own = Boolean(viewerId && metadata.createdBy === viewerId);
    const published = metadata.visibility === "published";
    const tenantOrigin = metadata.objectOrigin === "tenant";
    const originDeployment = metadata.objectOrigin === "origin_deployment";
    const visibleActive = metadata.hiddenAt == null && metadata.withdrawnAt == null;
    const hiddenActive = metadata.hiddenAt != null && metadata.withdrawnAt == null;
    const write = permission(permissions, PERMISSIONS.VIEWS_WRITE);
    const manage = permission(permissions, PERMISSIONS.VIEWS_MANAGE);
    const deploy = permission(permissions, PERMISSIONS.VIEWS_DEPLOY);
    const canManagePublished = manage && published && tenantOrigin;
    const loaded = Boolean(view.definition && typeof view.definition === "object");
    const definition = loaded
        ? view.definition
        : {};

    const body = {
            ...definition,
            id: metadata.id,
            ...(metadata.kind === "kernel"
                ? {
                    command: metadata.name,
                    scopeMode: metadata.scopeMode || definition.scopeMode,
                }
                : { slug: metadata.name }),
            title: metadata.title || definition.title || metadata.name,
            tenantViewId: metadata.id,
            version: metadata.version,
            visibility: metadata.visibility,
            createdBy: metadata.createdBy,
            createdByLabel: metadata.createdByLabel,
            objectOrigin: metadata.objectOrigin,
            sourceArtifactId: metadata.sourceArtifactId,
            forkedFrom: metadata.forkedFrom,
            formatVersion: metadata.formatVersion,
            stepKinds: metadata.stepKinds,
            renderKind: metadata.renderKind,
            originDeployedAt: metadata.originDeployedAt,
            withdrawnAt: metadata.withdrawnAt,
            hiddenAt: metadata.hiddenAt,
            // Local Studio records use epoch milliseconds. Normalize the wire
            // timestamps at the cloud boundary so mixed catalogs sort and
            // render relative times consistently.
            createdAt: numericTimestamp(metadata.createdAt),
            updatedAt: numericTimestamp(metadata.updatedAt),
            bodyLoaded: loaded,
            canEdit: tenantOrigin && ((own && write) || canManagePublished),
            canDelete: tenantOrigin && ((own && write) || canManagePublished),
            canForceDelete: manage && tenantOrigin,
            canFork: write && published,
            canPublish: tenantOrigin && own && deploy,
            canOverrideSecrets: tenantOrigin && manage,
            canUnpublish: tenantOrigin && ((own && deploy) || canManagePublished),
            canHide: manage && originDeployment && visibleActive,
            canUnhide: manage && originDeployment && hiddenActive,
    };
    if (loaded) storedBodies.add(body);
    if (bucket) storedBodyBuckets.set(body, bucket);
    return {
        body,
        metadata: {
            ...metadata,
            viewerOwned: own && tenantOrigin,
        },
        definitionJson: view.definitionJson,
    };
}

export function isStoredTenantViewBody(body) {
    return Boolean(body && typeof body === "object" && storedBodies.has(body));
}

async function listAllMetadata(signal, { includeHidden = false } = {}) {
    const rows = [];
    const seenTokens = new Set();
    let pageToken = "";
    do {
        const page = await listTenantViews({
            pageSize: PAGE_SIZE,
            pageToken,
            includeHidden,
            signal,
        });
        rows.push(...page.tenantViews);
        pageToken = page.nextPageToken || "";
        if (pageToken) {
            if (seenTokens.has(pageToken)) {
                throw new Error("tenant-view catalog pagination repeated a page token");
            }
            seenTokens.add(pageToken);
        }
    } while (pageToken);
    return rows;
}

export async function loadTenantViewCloud(tenantId, { force = false, signal } = {}) {
    const requestIdentityGeneration = identityGeneration;
    let resolvedTenantId = tenantId;
    const userPromise = getCurrentUser();
    if (!resolvedTenantId) {
        resolvedTenantId = (await userPromise)?.currentTenant?.tenantId || null;
    }
    if (requestIdentityGeneration !== identityGeneration) return EMPTY;
    if (!resolvedTenantId) return EMPTY;
    activeTenantId = resolvedTenantId;

    const key = tenantKey(resolvedTenantId);
    const existing = buckets.get(key);
    if (!force && existing?.inflight) return existing.inflight;
    if (!force && existing?.snapshot?.status === "ready") return existing.snapshot;

    const loading = {
        tenantId: resolvedTenantId,
        status: "loading",
        capabilities: existing?.snapshot?.capabilities || null,
        rows: existing?.snapshot?.rows || [],
        error: null,
    };
    const bucket = existing || {};
    if (existing?.snapshot && existing.snapshot.status !== "loading") {
        bucket.settledSnapshot = existing.snapshot;
    }
    const loadSequence = (bucket.loadSequence || 0) + 1;
    bucket.loadSequence = loadSequence;
    bucket.snapshot = loading;
    buckets.set(key, bucket);
    notify();

    const inflight = Promise.all([
        userPromise,
        getMyPermissions(),
        getTenantViewCapabilities({ force, signal }),
    ]).then(async ([user, permissions, capabilities]) => {
        const metadata = await listAllMetadata(signal, {
            includeHidden: permission(permissions, PERMISSIONS.VIEWS_MANAGE),
        });
        if (
            requestIdentityGeneration !== identityGeneration
            || bucket.loadSequence !== loadSequence
        ) return bucket.snapshot || EMPTY;
        bucket.viewerId = user?.userId || null;
        bucket.permissions = permissions;
        bucket.bodyInflight ||= new Map();
        const priorById = new Map((bucket.snapshot?.rows || []).map(
            (row) => [row.metadata.id, row],
        ));
        const rows = metadata.map((item) => {
            const prior = priorById.get(item.id);
            if (prior?.body?.bodyLoaded && prior.metadata.version === item.version) {
                return presentation(
                    {
                        metadata: item,
                        definition: prior.body,
                        definitionJson: prior.definitionJson,
                    },
                    user?.userId || null,
                    permissions,
                    bucket,
                );
            }
            return presentation(
                { metadata: item, definition: null, definitionJson: null },
                user?.userId || null,
                permissions,
                bucket,
            );
        });
        const snapshot = {
            tenantId: resolvedTenantId,
            status: "ready",
            capabilities,
            rows,
            error: null,
        };
        bucket.snapshot = snapshot;
        bucket.settledSnapshot = snapshot;
        notify();
        return bucket.snapshot;
    }).catch((error) => {
        if (
            requestIdentityGeneration !== identityGeneration
            || bucket.loadSequence !== loadSequence
        ) return bucket.snapshot || EMPTY;
        bucket.snapshot = {
            tenantId: resolvedTenantId,
            status: "unavailable",
            capabilities: null,
            rows: existing?.snapshot?.rows || [],
            error,
        };
        bucket.settledSnapshot = bucket.snapshot;
        notify();
        return bucket.snapshot;
    }).finally(() => {
        if (bucket.inflight === inflight) bucket.inflight = null;
    });
    bucket.inflight = inflight;

    return inflight;
}

function bucketContainingId(tenantViewId, tenantId = null) {
    const preferred = buckets.get(tenantKey(tenantId));
    if (preferred?.snapshot?.rows?.some((row) => row.metadata.id === tenantViewId)) {
        return preferred;
    }
    return null;
}

function canvasDependencyIds(definition) {
    const ids = new Set();
    for (const row of Array.isArray(definition?.rows) ? definition.rows : []) {
        for (const cell of Array.isArray(row?.cells) ? row.cells : []) {
            const id = cell?.kernel_id ?? cell?.kernelId;
            if (typeof id === "string" && id) ids.add(id);
        }
    }
    return [...ids];
}

async function loadCanvasDependencies(bucket, definition, { tenantId, signal }) {
    const ids = canvasDependencyIds(definition).filter((id) =>
        bucket.snapshot.rows.some((row) => row.metadata.id === id));
    let next = 0;
    const worker = async () => {
        while (next < ids.length) {
            const index = next;
            next += 1;
            await loadTenantViewBody(ids[index], { tenantId, signal });
        }
    };
    await Promise.all(
        Array.from(
            { length: Math.min(DEPENDENCY_GET_CONCURRENCY, ids.length) },
            () => worker(),
        ),
    );
}

export async function loadTenantViewBody(
    tenantViewId,
    { tenantId = null, signal, withDependencies = false } = {},
) {
    const requestIdentityGeneration = identityGeneration;
    const bucket = bucketContainingId(tenantViewId, tenantId);
    if (!bucket) throw new Error("tenant view is not in the readable catalog");
    const existing = bucket.snapshot.rows.find((row) => row.metadata.id === tenantViewId);
    if (existing?.body?.bodyLoaded) {
        if (withDependencies && existing.metadata.kind === "canvas") {
            await loadCanvasDependencies(bucket, existing.body, { tenantId, signal });
        }
        return existing.body;
    }
    bucket.bodyInflight ||= new Map();
    const shared = !signal;
    if (shared && bucket.bodyInflight.has(tenantViewId)) {
        return bucket.bodyInflight.get(tenantViewId);
    }
    const inflight = (async () => {
        for (let attempt = 0; attempt < BODY_LOAD_ATTEMPTS; attempt += 1) {
            const view = await getTenantView(tenantViewId, { signal });
            if (requestIdentityGeneration !== identityGeneration) {
                throw new Error("tenant view identity changed while its body was loading");
            }
            const row = presentation(
                view,
                bucket.viewerId,
                bucket.permissions,
                bucket,
            );
            let current = bucket.snapshot.rows.find(
                (candidate) => candidate.metadata.id === tenantViewId,
            );
            if (!current) {
                throw new Error("tenant view left the readable catalog while its body was loading");
            }
            const fetchedVersionOrder = compareVersions(
                row.metadata.version,
                current.metadata.version,
            );
            if (fetchedVersionOrder < 0) {
                if (current.body.bodyLoaded) {
                    const currentVersion = String(current.metadata.version);
                    if (withDependencies && current.metadata.kind === "canvas") {
                        await loadCanvasDependencies(
                            bucket,
                            current.body,
                            { tenantId, signal },
                        );
                    }
                    const afterDependencies = bucket.snapshot.rows.find(
                        (candidate) => candidate.metadata.id === tenantViewId,
                    );
                    if (
                        afterDependencies?.body?.bodyLoaded
                        && String(afterDependencies.metadata.version) === currentVersion
                    ) {
                        return afterDependencies.body;
                    }
                }
                continue;
            }
            const loadNewerCanvasDependencies =
                fetchedVersionOrder > 0 && row.metadata.kind === "canvas";
            if (loadNewerCanvasDependencies) {
                await refreshNewCanvasDependencyCatalog(bucket, row.body, { signal });
            }
            if (
                row.metadata.kind === "canvas"
                && (withDependencies || loadNewerCanvasDependencies)
            ) {
                await loadCanvasDependencies(bucket, row.body, { tenantId, signal });
                current = bucket.snapshot.rows.find(
                    (candidate) => candidate.metadata.id === tenantViewId,
                );
                if (!current) {
                    throw new Error(
                        "tenant view left the readable catalog while its dependencies were loading",
                    );
                }
                const afterDependencyVersionOrder = compareVersions(
                    row.metadata.version,
                    current.metadata.version,
                );
                if (afterDependencyVersionOrder < 0) {
                    if (current.body.bodyLoaded) return current.body;
                    continue;
                }
            }
            bucket.snapshot = {
                ...bucket.snapshot,
                rows: bucket.snapshot.rows.map(
                    (candidate) =>
                        candidate.metadata.id === tenantViewId
                        && compareVersions(
                            row.metadata.version,
                            candidate.metadata.version,
                        ) >= 0
                            ? row
                            : candidate,
                ),
            };
            notify();
            return row.body;
        }
        throw new Error("tenant view changed repeatedly while its body was loading");
    })().finally(() => {
        if (shared && bucket.bodyInflight.get(tenantViewId) === inflight) {
            bucket.bodyInflight.delete(tenantViewId);
        }
    });
    if (shared) bucket.bodyInflight.set(tenantViewId, inflight);
    return inflight;
}

function rowsFor(tenantId, kind) {
    return getTenantViewCloudState(tenantId).rows.filter(
        (row) => row.metadata.kind === kind
            && row.metadata.hiddenAt == null
            && row.metadata.withdrawnAt == null,
    );
}

function kernelRows(tenantId, { loadedOnly = false } = {}) {
    const rows = rowsFor(tenantId, "kernel");
    const eligible = loadedOnly
        ? rows.filter((row) => row.body.bodyLoaded)
        : rows;
    return {
        viewerOwned: eligible.filter((row) => row.metadata.viewerOwned),
        tenantVisible: eligible.filter(
            (row) => !row.metadata.viewerOwned && row.metadata.visibility === "published",
        ),
    };
}

export function listCloudKernelRows(tenantId) {
    return kernelRows(tenantId, { loadedOnly: true });
}

export function listCloudKernelCatalogRows(tenantId) {
    return kernelRows(tenantId);
}

export function listCloudCanvases(tenantId) {
    const rows = rowsFor(tenantId, "canvas");
    return {
        viewerOwned: rows.filter((row) => row.metadata.viewerOwned).map((row) => row.body),
        tenantVisible: rows
            .filter((row) => !row.metadata.viewerOwned && row.metadata.visibility === "published")
            .map((row) => row.body),
    };
}

/**
 * Hidden Origin-deployed rows are management records only. They deliberately
 * stay outside the executable kernel/canvas catalogs so they cannot resolve or
 * run while hidden.
 */
export function listHiddenOriginDeploymentViews(tenantId, kind = null) {
    return getTenantViewCloudState(tenantId).rows
        .filter((row) =>
            row.metadata.objectOrigin === "origin_deployment"
            && (!kind || row.metadata.kind === kind)
            && row.metadata.hiddenAt != null
            && row.metadata.withdrawnAt == null)
        .map((row) => row.body);
}

function bucketForBody(body) {
    const mapped = body && typeof body === "object"
        ? storedBodyBuckets.get(kernelSnapshotSourceBody(body))
        : null;
    if (mapped) return mapped;
    const tenantId = body?.tenantId;
    return tenantId ? buckets.get(tenantKey(tenantId)) || null : null;
}

function activeBucket() {
    return activeTenantId ? buckets.get(tenantKey(activeTenantId)) || null : null;
}

function captureMutationContext(body = null) {
    const bucket = body ? bucketForBody(body) : activeBucket();
    if (AUTH_REQUIRED && !bucket?.snapshot?.tenantId) {
        throw new Error("tenant view identity is unavailable");
    }
    const context = { bucket, generation: identityGeneration };
    assertMutationContext(context);
    return context;
}

function assertMutationContext(context) {
    if (context?.generation !== identityGeneration) {
        throw new Error("tenant view identity changed while the operation was in flight");
    }
    if (!context?.bucket) return null;
    const tenantId = context?.bucket?.snapshot?.tenantId;
    if (
        !tenantId
        || buckets.get(tenantKey(tenantId)) !== context.bucket
    ) {
        throw new Error("tenant view identity changed while the operation was in flight");
    }
    return context.bucket;
}

function upsertView(view, context) {
    const bucket = assertMutationContext(context);
    if (!bucket?.snapshot) return view;
    invalidateCatalogLoad(bucket);
    const row = presentation(
        view,
        bucket.viewerId,
        bucket.permissions,
        bucket,
    );
    bucket.snapshot = {
        ...bucket.snapshot,
        status: "ready",
        error: null,
        rows: [
            ...bucket.snapshot.rows.filter((entry) => entry.body.tenantViewId !== view.metadata.id),
            row,
        ],
    };
    bucket.settledSnapshot = bucket.snapshot;
    notify();
    return row.body;
}

export function tenantViewDefinitionFromBody(body) {
    const definition = { ...body };
    for (const key of [
        "id",
        "builtin",
        "createdAt",
        "updatedAt",
        "tenantViewId",
        "version",
        "visibility",
        "createdBy",
        "createdByLabel",
        "objectOrigin",
        "sourceArtifactId",
        "forkedFrom",
        "formatVersion",
        "stepKinds",
        "renderKind",
        "originDeployedAt",
        "withdrawnAt",
        "hiddenAt",
        "canEdit",
        "canDelete",
        "canForceDelete",
        "canFork",
        "canPublish",
        "canUnpublish",
        "canHide",
        "canUnhide",
        "canOverrideSecrets",
        "bodyLoaded",
    ]) {
        delete definition[key];
    }
    return definition;
}

export function tenantViewDefinitionDiffers(left, right) {
    if (left === right) return false;
    if (!left || !right) return true;
    return JSON.stringify(tenantViewDefinitionFromBody(left))
        !== JSON.stringify(tenantViewDefinitionFromBody(right));
}

export function prepareCloudTenantViewCreate(
    kind,
    definition,
    { capabilities } = {},
) {
    const name = kind === "kernel" ? definition.command : definition.slug;
    // A stale dashboard tab can observe a server that already advertises a
    // newer definition format. This client must never claim to have authored
    // bytes for a schema it does not implement.
    const formatVersion = Math.min(
        KERNEL_CANVAS_ARTIFACT_FORMAT_VERSION,
        Number(capabilities?.currentFormatVersion ?? KERNEL_CANVAS_ARTIFACT_FORMAT_VERSION),
    );
    return Object.freeze({
        kind,
        name,
        title: definition.title || name,
        formatVersion,
        definitionJson: JSON.stringify(tenantViewDefinitionFromBody(definition)),
    });
}

export async function executeCloudTenantViewCreate(
    request,
    { capabilities, createRequestId } = {},
) {
    if (!createRequestId) {
        throw new Error("durable createRequestId is required before creating a tenant view");
    }
    const context = captureMutationContext();
    const view = await createTenantView({
        ...request,
        createRequestId,
    }, { capabilities });
    return upsertView(view, context);
}

export async function createCloudTenantView(
    kind,
    definition,
    { capabilities, createRequestId } = {},
) {
    return executeCloudTenantViewCreate(
        prepareCloudTenantViewCreate(kind, definition, { capabilities }),
        { capabilities, createRequestId },
    );
}

async function refreshNewCanvasDependencyCatalog(bucket, definition, { signal }) {
    const dependencyIds = canvasDependencyIds(definition);
    const missingBeforeRefresh = dependencyIds.filter((id) =>
        !bucket.snapshot.rows.some((row) => row.metadata.id === id));
    if (missingBeforeRefresh.length === 0) return;
    await loadTenantViewCloud(bucket.snapshot.tenantId, { force: true, signal });
    const missingAfterRefresh = dependencyIds.filter((id) =>
        !bucket.snapshot.rows.some((row) => row.metadata.id === id));
    if (missingAfterRefresh.length > 0) {
        throw new Error("canvas dependency is not in the readable catalog");
    }
}

export async function updateCloudTenantView(body, definition = body) {
    const context = captureMutationContext(body);
    const kind = body.command ? "kernel" : "canvas";
    const name = kind === "kernel" ? definition.command : definition.slug;
    const baseDefinition = tenantViewDefinitionFromBody(body);
    const editedDefinition = tenantViewDefinitionFromBody(definition);
    const baseFormatVersion = Number(
        body.formatVersion ?? KERNEL_CANVAS_ARTIFACT_FORMAT_VERSION,
    );
    const editedFormatVersion = Number(
        definition.formatVersion ?? baseFormatVersion,
    );
    const definitionForWrite = editedFormatVersion === baseFormatVersion
        ? mergeSameVersionTenantViewDefinition(baseDefinition, editedDefinition)
        : editedDefinition;
    const view = await updateTenantView({
        tenantViewId: body.tenantViewId,
        expectedVersion: body.version,
        kind,
        visibility: body.visibility,
        name,
        title: definition.title || name,
        formatVersion: editedFormatVersion,
        definitionJson: JSON.stringify(definitionForWrite),
    });
    return upsertView(view, context);
}

function isDeletedIdempotentForkResult(error) {
    return (
        error?.code === "FAILED_PRECONDITION"
        && error?.serverMessage === "the idempotent create result was deleted"
    );
}

export async function forkCloudTenantView(body) {
    const context = captureMutationContext(body.builtin === true ? null : body);
    const kind = body.command ? "kernel" : "canvas";
    const sourceId = body.builtin === true ? body.id : body.tenantViewId;
    const sourceVersion = body.builtin === true ? "bundled" : body.version;
    const tenantId = context.bucket.snapshot.tenantId;
    const journal = openTenantViewWriteJournal({
        tenantId,
        operation: "fork",
        kind,
        sourceKey: `${sourceId}:${sourceVersion}`,
    });
    const requestFor = (pending) => body.builtin === true
        ? {
            bundled: {
                sourceId: body.id,
            },
            createRequestId: pending.createRequestId,
        }
        : {
            stored: {
                tenantViewId: body.tenantViewId,
                expectedVersion: body.version,
            },
            createRequestId: pending.createRequestId,
        };
    let pending = journal.begin();
    let view;
    try {
        view = await forkTenantView(requestFor(pending));
    } catch (error) {
        assertMutationContext(context);
        if (!isDeletedIdempotentForkResult(error)) throw error;
        // The server has definitively told us that this request id once
        // completed and its result was later deleted. Replaying it can never
        // recover a row, so retire only this dead ledger entry and retry once
        // with a newly persisted id. Unavailable, transport, and all other
        // ambiguous failures keep the old id for lost-response safety.
        journal.clear();
        pending = journal.begin();
        view = await forkTenantView(requestFor(pending));
    }
    const updated = upsertView(view, context);
    assertMutationContext(context);
    journal.complete(pending, updated.tenantViewId, updated.version);
    journal.clear();
    return updated;
}

async function mutateCloudTenantView(body, mutation, options) {
    const context = captureMutationContext(body);
    const view = options === undefined
        ? await mutation(body.tenantViewId, body.version)
        : await mutation(body.tenantViewId, body.version, options);
    const updated = upsertView(view, context);
    if (Array.isArray(view.visibleDependents)) {
        Object.defineProperty(updated, "visibleDependents", {
            configurable: true,
            enumerable: false,
            value: view.visibleDependents,
        });
    }
    return updated;
}

export const publishCloudTenantView = (body, options) =>
    mutateCloudTenantView(body, publishTenantView, options);
export const unpublishCloudTenantView = (body) =>
    mutateCloudTenantView(body, unpublishTenantView);
export const hideCloudTenantView = (body) =>
    mutateCloudTenantView(body, hideTenantView);
export const unhideCloudTenantView = (body) =>
    mutateCloudTenantView(body, unhideTenantView);

export async function deleteCloudTenantView(body, { force = false } = {}) {
    const context = captureMutationContext(body);
    await deleteTenantView(body.tenantViewId, body.version, { force });
    const bucket = assertMutationContext(context);
    if (bucket?.snapshot) {
        invalidateCatalogLoad(bucket);
        bucket.snapshot = {
            ...bucket.snapshot,
            rows: bucket.snapshot.rows.filter(
                (row) => row.body.tenantViewId !== body.tenantViewId,
            ),
        };
        bucket.settledSnapshot = bucket.snapshot;
        notify();
    }
}

export async function listCloudTenantViewDependents(body, options) {
    const context = captureMutationContext(body);
    const result = await listTenantViewDependents(body.tenantViewId, options);
    assertMutationContext(context);
    return result;
}

export async function listAllCloudTenantViewDependents(
    body,
    { pageSize = 200, signal } = {},
) {
    const dependents = [];
    const seenTokens = new Set();
    let pageToken;
    do {
        const page = await listCloudTenantViewDependents(body, {
            pageSize,
            pageToken,
            signal,
        });
        dependents.push(...page.dependents);
        pageToken = page.nextPageToken || "";
        if (pageToken) {
            if (seenTokens.has(pageToken)) {
                throw new Error("dependent-list pagination repeated a page token");
            }
            seenTokens.add(pageToken);
        }
    } while (pageToken);
    return dependents;
}

export function _resetTenantViewCloudForTests() {
    resetTenantViewCloudIdentityState();
    listeners.clear();
}
